import { ChartOptions, TooltipItem } from 'chart.js';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import Utils from '../../../../utilities/utils';
import { HeadwayDistribution } from './StatsHeadwaysForm';

const chartSettings = {
    labels: [] as string[][],
    datasets: [
        {
            label: 'Very Short (< 5 min)',
            lineTension: 0.1,
            backgroundColor: 'rgba(130, 225, 128, 0.8)',
            borderColor: '#82E180',
            borderDash: [],
            borderDashOffset: 0.0,
            pointBorderColor: '#82E180',
            pointBackgroundColor: '#82E180',
            pointBorderWidth: 1,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#82E180',
            pointHoverBorderColor: '#82E180',
            pointHoverBorderWidth: 2,
            pointRadius: 5,
            pointHitRadius: 10,
            data: [] as { x: string; y: number }[],
        },
        {
            label: 'Short (5-10 min)',
            lineTension: 0.1,
            backgroundColor: 'rgba(49, 220, 195, 0.8)',
            borderColor: '#31DCC3',
            borderDash: [],
            borderDashOffset: 0.0,
            pointBorderColor: '#31DCC3',
            pointBackgroundColor: '#31DCC3',
            pointBorderWidth: 1,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#31DCC3',
            pointHoverBorderColor: '#31DCC3',
            pointHoverBorderWidth: 2,
            pointRadius: 5,
            pointHitRadius: 10,
            data: [] as { x: string; y: number }[],
        },
        {
            label: 'Medium (10-20 min)',
            lineTension: 0.1,
            backgroundColor: 'rgba(255, 192, 97, 0.8)',
            borderColor: '#FFC061',
            borderDash: [],
            borderDashOffset: 0.0,
            pointBorderColor: '#FFC061',
            pointBackgroundColor: '#FFC061',
            pointBorderWidth: 1,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#FFC061',
            pointHoverBorderColor: '#FFC061',
            pointHoverBorderWidth: 2,
            pointRadius: 5,
            pointHitRadius: 10,
            data: [] as { x: string; y: number }[],
        },
        {
            label: 'Long (> 20 min)',
            lineTension: 0.1,
            backgroundColor: 'rgba(255, 131, 104, 0.8)',
            borderColor: '#FF8368',
            borderDash: [],
            borderDashOffset: 0.0,
            pointBorderColor: '#FF8368',
            pointBackgroundColor: '#FF8368',
            pointBorderWidth: 1,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#FF8368',
            pointHoverBorderColor: '#FF8368',
            pointHoverBorderWidth: 2,
            pointRadius: 5,
            pointHitRadius: 10,
            data: [] as { x: string; y: number }[],
        },
    ],
};

const options: ChartOptions<'line'> = {
    scales: {
        x: {
            display: false,
            labels: [],
            ticks: {
                autoSkip: false,
                maxRotation: 0,
            },
        },
        x2: {
            grid: {
                display: false,
            },
            border: {
                display: false,
            },
            ticks: {
                autoSkip: false,
                maxRotation: 0,
            },
        },
        y: {
            beginAtZero: true,
            ticks: {
                autoSkip: false,
                callback: function (tickValue: string | number) {
                    return `${tickValue} %`;
                },
                stepSize: 1,
                maxTicksLimit: 10,
            },
            stacked: true,
        },
    },
    hover: {
        mode: 'x' as const,
        intersect: false,
    },
    elements: {
        line: {
            tension: 0.000001,
        },
    },
    plugins: {
        title: {
            display: false,
        },
        legend: {
            display: true,
            position: 'bottom' as const,
            labels: {
                usePointStyle: true,
            },
        },
        tooltip: {
            mode: 'x' as const,
            intersect: false,
            callbacks: {
                title: function (tooltipItem: TooltipItem<'line'>[]) {
                    return `${tooltipItem[0].parsed.x}`;
                },
                label: function (tooltipItem: TooltipItem<'line'>) {
                    let lineType = 'Very Short (< 5 min)';
                    if (tooltipItem.datasetIndex === 1)
                        lineType = 'Short (5-10 min)';
                    else if (tooltipItem.datasetIndex === 2)
                        lineType = 'Medium (10-20 min)';
                    else if (tooltipItem.datasetIndex === 3)
                        lineType = 'Long (> 20 min)';
                    return `${lineType}: ${Utils.roundNumber(tooltipItem.parsed.y, 1)} %`;
                },
                labelColor: function (tooltipItem: TooltipItem<'line'>) {
                    if (tooltipItem.datasetIndex === 0) {
                        return {
                            borderColor: '#82E180',
                            backgroundColor: '#82E180',
                        };
                    } else if (tooltipItem.datasetIndex === 1) {
                        return {
                            borderColor: '#31DCC3',
                            backgroundColor: '#31DCC3',
                        };
                    } else if (tooltipItem.datasetIndex === 2) {
                        return {
                            borderColor: '#FFC061',
                            backgroundColor: '#FFC061',
                        };
                    } else {
                        return {
                            borderColor: '#FF8368',
                            backgroundColor: '#FF8368',
                        };
                    }
                },
            },
        },
        filler: {
            propagate: true,
        },
    },
};

const HeadwayDistributionChart: React.FC<{ chartData: HeadwayDistribution[] }> = ({ chartData }) => {
    const [dataState, setDataState] = useState(chartSettings);

    useEffect(() => {
        setDataState(prevState => ({
            ...prevState,
            labels: chartData.map(d => d.period.split(/(?=-)/g)),
            datasets: [
                {
                    ...prevState.datasets[0], data: chartData.map(d => {
                        return {
                            x: d.period,
                            y: d.veryShort,
                        };
                    }),
                },
                {
                    ...prevState.datasets[1], data: chartData.map(d => {
                        return {
                            x: d.period,
                            y: d.short,
                        };
                    }),
                },
                {
                    ...prevState.datasets[2], data: chartData.map(d => {
                        return {
                            x: d.period,
                            y: d.medium,
                        };
                    }),
                },
                {
                    ...prevState.datasets[3], data: chartData.map(d => {
                        return {
                            x: d.period,
                            y: d.long,
                        };
                    }),
                },
            ],
        }));
    }, [chartData]);

    return (
        <Line
            data={dataState}
            height={150}
            options={options}
        />
    );
};

export default HeadwayDistributionChart;
